import { initializeApp, type FirebaseApp } from "firebase/app";
import { getMessaging, getToken, isSupported } from "firebase/messaging";
import { inject, injectable } from "inversify";
import { NotificationApi, RegisterFCMRequestOsEnum } from "@generated/n3-backend-api";
import NotificationService from "@/src/application/services/notification-service";

const TOKEN_STORAGE_KEY = "waterbureau:fcm-token";

function getFirebaseConfig() {
  return {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
    measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
  };
}

function hasFirebaseConfig() {
  const config = getFirebaseConfig();
  return Boolean(config.apiKey && config.appId && config.messagingSenderId && config.projectId);
}

@injectable()
export default class FirebaseNotificationService extends NotificationService {
  private app: FirebaseApp | null = null;
  private supportCheck: Promise<boolean> | null = null;

  constructor(@inject(NotificationApi) private readonly notificationApi: NotificationApi) {
    super();
  }

  isSupported(): boolean {
    return hasFirebaseConfig() && "Notification" in window && "serviceWorker" in navigator;
  }

  isRegistered(): boolean {
    return (
      this.isSupported() &&
      Notification.permission === "granted" &&
      Boolean(localStorage.getItem(TOKEN_STORAGE_KEY))
    );
  }

  async registerForPushNotifications(): Promise<void> {
    if (!this.isSupported()) {
      throw new Error("Push notifications are not supported in this browser.");
    }

    const permission = await Notification.requestPermission();
    if (permission !== "granted") {
      throw new Error("Notification permission was not granted.");
    }

    if (!(await this.isFirebaseMessagingSupported())) {
      throw new Error("Firebase messaging is not supported in this browser.");
    }

    const registration = await navigator.serviceWorker.register("/firebase-messaging-sw.js");
    const messaging = getMessaging(this.getApp());
    const token = await getToken(messaging, {
      vapidKey: import.meta.env.VITE_FIREBASE_VAPID_KEY,
      serviceWorkerRegistration: registration,
    });

    if (!token) {
      throw new Error("Could not get Firebase notification token.");
    }

    await this.registerToken(token);
  }

  async unregisterPushNotifications(): Promise<void> {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    if (!token) {
      return;
    }

    await this.notificationApi.unregisterToken({ token });
    localStorage.removeItem(TOKEN_STORAGE_KEY);
  }

  private getApp() {
    if (!this.app) {
      this.app = initializeApp(getFirebaseConfig());
    }
    return this.app;
  }

  private isFirebaseMessagingSupported() {
    if (!this.supportCheck) {
      this.supportCheck = isSupported();
    }
    return this.supportCheck;
  }

  private async registerToken(newToken: string) {
    const oldToken = localStorage.getItem(TOKEN_STORAGE_KEY) ?? undefined;
    await this.notificationApi.registerToken({
      newToken,
      oldToken,
      os: RegisterFCMRequestOsEnum.Web,
    });
    localStorage.setItem(TOKEN_STORAGE_KEY, newToken);
  }
}
